import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { faAngleDown, faSpinner, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormCheckbox, TextField } from '@tryhackme/thm-ui-components';
/* eslint-disable unicorn/no-useless-undefined */
import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'src/common/form';
import type { RoomBlueprint } from 'src/common/interfaces/room-blueprint';
import { ACCORDION_ANIMATION_DURATION } from 'src/features/room/components/tasks/components/accordion/accordion.constants';
import { StyledRow } from '../../../components/nested-rule-condition/nested-rule.styles';
import { StyledFormGroup } from '../../../components/rule/rule.styles';
import { useGetBlueprintRuleQuery } from '../../blueprint.slice';
import { StyledLoadingSpinner } from '../../blueprint.styles';
import { StyledBody, StyledButton, StyledCard, StyledCheckbox, StyledChevronIcon, StyledIcon, StyledIconWrapper, StyledSummary, StyledSummaryHeader, StyledTitle, StyledWrapper } from './step.styles';
import type { BlueprintProps } from './step.types';
export const BlueprintStep = ({
  step,
  tasks,
  index,
  handleRemove
}: BlueprintProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(true);
  const {
    control,
    register,
    setValue,
    getValues
  } = useFormContext<RoomBlueprint>();
  const [taskId, questionNo, isAutoAnswer] = useWatch({
    control,
    name: [`steps.${index}.taskId`, `steps.${index}.questionNo`, `steps.${index}.isAutoAnswer`]
  });
  const [appendToQuestion, setAppendToQuestion] = useState(!!getValues(`steps.${index}.questionNo`));
  const {
    data: rule,
    isLoading
  } = useGetBlueprintRuleQuery(step.ruleId);
  const selectedQuestion = useMemo(() => tasks.filter(option => option.value === `${taskId ?? ''}-${questionNo ?? ''}`), [questionNo, taskId, tasks]);
  if (isLoading) {
    return <StyledLoadingSpinner icon={faSpinner} />;
  }
  const handleClick = () => setIsOpen(!isOpen);
  const handleCheckedChange = () => {
    setAppendToQuestion(!appendToQuestion);
    setValue(`steps.${index}.taskId`, undefined);
    setValue(`steps.${index}.questionNo`, undefined);
    setValue(`steps.${index}.isAutoAnswer`, false);
  };
  const handleQuestionSelection = (id: string) => {
    const [task, question] = id.split('-');
    setValue(`steps.${index}.taskId`, task);
    setValue(`steps.${index}.questionNo`, Number(question));
  };
  const order = index + 1;
  return <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-component="BlueprintStep" data-sentry-source-file="step.tsx">
      <StyledSummary role="button" aria-expanded={isOpen} data-sentry-element="StyledSummary" data-sentry-source-file="step.tsx">
        <StyledSummaryHeader onClick={handleClick} data-sentry-element="StyledSummaryHeader" data-sentry-source-file="step.tsx">
          <StyledIconWrapper data-sentry-element="StyledIconWrapper" data-sentry-source-file="step.tsx">
            <StyledIcon icon={faCircle} data-sentry-element="StyledIcon" data-sentry-source-file="step.tsx" />
            <span>{order}</span>
          </StyledIconWrapper>
          <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="step.tsx">
            Step {order} - {rule?.category}
          </StyledTitle>
        </StyledSummaryHeader>

        <StyledButton variant="outlined" color="secondary" size="small" onClick={handleRemove} data-sentry-element="StyledButton" data-sentry-source-file="step.tsx">
          <FontAwesomeIcon icon={faTrashCan} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="step.tsx" />
          Remove
        </StyledButton>

        <StyledChevronIcon isOpen={isOpen} icon={faAngleDown} onClick={handleClick} data-sentry-element="StyledChevronIcon" data-sentry-source-file="step.tsx" />
      </StyledSummary>
      <StyledBody nodeRef={ref} in={isOpen} timeout={ACCORDION_ANIMATION_DURATION} unmountOnExit data-sentry-element="StyledBody" data-sentry-source-file="step.tsx">
        <StyledCard data-sentry-element="StyledCard" data-sentry-source-file="step.tsx">
          {rule?.description && <StyledRow>
              <StyledFormGroup>
                <p>{rule.description}</p>
              </StyledFormGroup>
            </StyledRow>}

          <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="step.tsx">
            <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="step.tsx">
              <TextField variant="input" isDisabled value={rule?.title} data-sentry-element="TextField" data-sentry-source-file="step.tsx" />
            </StyledFormGroup>
          </StyledRow>

          {rule?.parameters.map(parameter => <StyledRow key={parameter}>
              <StyledFormGroup>
                <TextField {...register(`steps.${index}.parameters.${parameter}`)} id={`parameters-${parameter}`} placeholder="Value" variant="input" label={`{${parameter}}`} labelPosition="left" isDisabled={isLoading} isRequired />
              </StyledFormGroup>
            </StyledRow>)}

          <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="step.tsx">
            <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="step.tsx">
              <StyledCheckbox data-sentry-element="StyledCheckbox" data-sentry-source-file="step.tsx">
                <FormCheckbox checked={appendToQuestion} onCheckedChange={handleCheckedChange} data-sentry-element="FormCheckbox" data-sentry-source-file="step.tsx" />
                <span>Append to Question</span>
              </StyledCheckbox>

              {appendToQuestion && <>
                  <TextField placeholder="Select a question" variant="select" options={tasks} selected={selectedQuestion} isSearchable isRequired handleSelection={option => handleQuestionSelection(option.value)} />
                  <StyledCheckbox>
                    <FormCheckbox checked={isAutoAnswer} disabled={!taskId || !questionNo} onCheckedChange={() => setValue(`steps.${index}.isAutoAnswer`, !isAutoAnswer)} />
                    <span>Auto-answer</span>
                  </StyledCheckbox>
                </>}
            </StyledFormGroup>
          </StyledRow>
        </StyledCard>
      </StyledBody>
    </StyledWrapper>;
};